import { useCallback, useContext, useRef } from 'react';

import { ExperienceTrackerContext } from '@atlassian/experience-tracker';

import { SOURCE_LOCATION } from '../../../../constants';
import { useMenuStateContext } from '../../../../context';

interface usePrefetchLoomReturn {
	triggerPrefetch: () => void;
}

/**
 * Hook to load Loom SDK asynchronously when triggered.
 * This prevents the Loom SDK bundles from being loaded until the user interacts with the menu.
 *
 * @returns Object containing triggerPrefetch function to load Loom SDK bundles
 */
export function usePrefetchLoom(): usePrefetchLoomReturn {
	const loadPromiseRef = useRef<Promise<void> | null>(null);
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { product, suggestionConfig, crossAppCreationConfig } = useMenuStateContext();

	const triggerPrefetch = useCallback(() => {
		if (loadPromiseRef.current) {
			return;
		}

		// Only load if either suggestion config or cross-app creation is enabled
		if (!suggestionConfig.enabled && !crossAppCreationConfig?.enabled) {
			return;
		}

		const { cloudId, environment } = crossAppCreationConfig?.enabled
			? crossAppCreationConfig
			: suggestionConfig.enabled
				? suggestionConfig
				: {};
		const finalEnvironment = environment || 'prod';

		const loadingPromise = (async () => {
			try {
				if (cloudId) {
					const [{ initLoomSDK }, { convertSourceProductToProductType }] = await Promise.all([
						import(
							/* webpackChunkName: "@atlaskit-internal_async-universal-create-loom-utils-initLoomSDK" */
							'@atlassian/loom-utils/initLoomSDK'
						),
						import(
							/* webpackChunkName: "@atlaskit-internal_async-universal-create-loom-config" */
							'@atlassian/universal-create-integrations/loom'
						),
					]);
					await initLoomSDK({
						environment: finalEnvironment,
						cloudId,
						productName: convertSourceProductToProductType(product),
						experienceTracker,
						entryPointLocation: SOURCE_LOCATION,
					});
				}
			} catch {
				// ignore
			}
		})();

		loadPromiseRef.current = loadingPromise;
	}, [experienceTracker, product, suggestionConfig, crossAppCreationConfig]);

	return {
		triggerPrefetch,
	};
}
